// src/services/office/adapters.ts
import { IOfficeContext, HostType } from '../../types/office';

/**
 * Formats Excel cell values as CSV format for API
 * @param worksheetName - Name of the worksheet
 * @param values - 2D array of cell values
 * @returns CSV formatted string representation of the data
 */
function formatExcelData(worksheetName: string, values: any[][]): string {
  if (!values || values.length === 0) {
    return `Worksheet: ${worksheetName}\n\n(Empty worksheet)`;
  }
  
  // Format each row as CSV
  const csvRows: string[] = [];
  
  for (let i = 0; i < values.length; i++) {
    const row = values[i];
    if (!row || row.length === 0) continue;
    
    // Format row with CSV escaping
    const formattedRow = row.map((cell: any) => {
      // Handle null, undefined, or empty cells
      if (cell === null || cell === undefined) {
        return "";
      }
      // Convert to string
      let cellValue = String(cell);
      
      // CSV escaping: if cell contains comma, quote, or newline, wrap in quotes and escape quotes
      if (cellValue.includes(',') || cellValue.includes('"') || cellValue.includes('\n') || cellValue.includes('\r')) {
        // Escape quotes by doubling them
        cellValue = cellValue.replace(/"/g, '""');
        // Wrap in quotes
        cellValue = `"${cellValue}"`;
      }
      
      return cellValue;
    });
    
    // Join cells with comma separator
    csvRows.push(formattedRow.join(','));
  }
  
  // Combine with header text
  const csvContent = csvRows.join('\n');
  return `The current worksheet data is as follows (in CSV format):\n${csvContent}`;
}

// --- Base Adapter for common logic ---
abstract class BaseAdapter implements IOfficeContext {
  host: HostType;
  metadata: { title?: string; sender?: string; userEmail?: string } = {};

  constructor(host: HostType, userEmail?: string) {
    this.host = host;
    this.metadata.userEmail = userEmail;
  }

  abstract getContent(): Promise<string>;
  abstract getSelection(): Promise<string>;
}

// --- Outlook Specific Adapter ---
export class OutlookAdapter extends BaseAdapter {
  private item: Office.MessageCompose | Office.MessageRead;

  constructor(item: Office.MessageCompose | Office.MessageRead, userEmail?: string) {
    super("Outlook", userEmail);
    this.item = item;
    
    // Extract Outlook-specific metadata immediately
    // Handle subject which can be string or Subject object
    if (item.subject) {
      this.metadata.title = typeof item.subject === "string" ? item.subject : item.subject.toString();
    }
    
    // Handle from which can be string, EmailAddressDetails, or From
    if ("from" in item && item.from) {
      if (typeof item.from === "string") {
        this.metadata.sender = item.from;
      } else if ("displayName" in item.from && item.from.displayName) {
        this.metadata.sender = item.from.displayName;
      } else if ("emailAddress" in item.from && item.from.emailAddress) {
        this.metadata.sender = item.from.emailAddress;
      }
    }
  }

  // Unified method to get Email Body
  async getContent(): Promise<string> {
    return new Promise((resolve, reject) => {
      // Coercion type Text (or HTML) based on what your LLM prefers
      this.item.body.getAsync(Office.CoercionType.Text, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          reject(result.error);
        }
      });
    });
  }

  async getSelection(): Promise<string> {
    // Outlook doesn't always support getSelectedDataAsync in read mode easily, 
    // but this serves as a fallback or implemented if needed.
    return new Promise((resolve) => {
      Office.context.document.getSelectedDataAsync(Office.CoercionType.Text, (result) => {
        resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value as string : "");
      });
    });
  }
}

// --- Word/Excel/PowerPoint Adapter ---
export class DocumentAdapter extends BaseAdapter {
  constructor(host: HostType, userEmail?: string) {
    console.log(`[DocumentAdapter] Creating adapter for host: ${host}`);
    console.log(`[DocumentAdapter] User email:`, userEmail || "N/A");
    console.log(`[DocumentAdapter] Office.context available:`, !!Office.context);
    console.log(`[DocumentAdapter] Office.context.document available:`, !!Office.context?.document);
    
    try {
      super(host, userEmail);
      console.log(`[DocumentAdapter] BaseAdapter constructor completed`);
      
      // Attempt to get file URL or name as title
      if (Office.context?.document) {
        this.metadata.title = Office.context.document.url || undefined;
        console.log(`[DocumentAdapter] Document URL/title:`, this.metadata.title || "N/A");
      } else {
        console.warn(`[DocumentAdapter] Office.context.document not available, cannot get title`);
      }
      
      console.log(`[DocumentAdapter] DocumentAdapter created successfully for ${host}`);
    } catch (error) {
      console.error(`[DocumentAdapter] Error in constructor:`, error);
      console.error(`[DocumentAdapter] Constructor error details:`, {
        error: error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        host,
        userEmail
      });
      throw error;
    }
  }

  // Unified method to get Document Body
  async getContent(): Promise<string> {
    console.log(`[DocumentAdapter] getContent() called for host: ${this.host}`);
    
    // Word-specific: Get Body Text using Word.run
    if (this.host === "Word") {
      console.log(`[DocumentAdapter] Word host detected, using Word.run API`);
      return new Promise((resolve) => {
        console.log(`[DocumentAdapter] Checking Word API availability...`);
        console.log(`[DocumentAdapter] typeof Word:`, typeof Word);
        
        if (typeof Word === 'undefined') {
          console.warn(`[DocumentAdapter] Word API not available (typeof Word === 'undefined')`);
          resolve("");
          return;
        }
        
        console.log(`[DocumentAdapter] Word API available, calling Word.run...`);
        Word.run(async (context) => {
          try {
            console.log(`[DocumentAdapter] Word.run context created, loading document body...`);
            const body = context.document.body;
            body.load("text");
            console.log(`[DocumentAdapter] Body loaded, syncing...`);
            await context.sync();
            const text = body.text || "";
            console.log(`[DocumentAdapter] Document body retrieved, length:`, text.length);
            resolve(text);
          } catch (error) {
            console.error(`[DocumentAdapter] Error in Word.run:`, error);
            console.error(`[DocumentAdapter] Word.run error details:`, {
              error: error,
              errorMessage: error instanceof Error ? error.message : String(error),
              errorStack: error instanceof Error ? error.stack : undefined
            });
            resolve("");
          }
        }).catch((error) => {
          // Fallback if Word.run fails
          console.error(`[DocumentAdapter] Word.run promise rejected:`, error);
          console.error(`[DocumentAdapter] Word.run rejection details:`, {
            error: error,
            errorMessage: error instanceof Error ? error.message : String(error),
            errorStack: error instanceof Error ? error.stack : undefined
          });
          resolve("");
        });
      });
    }
    
    // PowerPoint-specific: Get presentation content using PowerPoint.run
    if (this.host === "PowerPoint") {
      console.log(`[DocumentAdapter] PowerPoint host detected, using PowerPoint.run API`);
      return new Promise((resolve) => {
        console.log(`[DocumentAdapter] Checking PowerPoint API availability...`);
        console.log(`[DocumentAdapter] typeof PowerPoint:`, typeof PowerPoint);
        
        if (typeof PowerPoint === 'undefined') {
          console.warn(`[DocumentAdapter] PowerPoint API not available (typeof PowerPoint === 'undefined')`);
          resolve("");
          return;
        }
        
        console.log(`[DocumentAdapter] PowerPoint API available, calling PowerPoint.run...`);
        PowerPoint.run(async (context) => {
          try {
            console.log(`[DocumentAdapter] PowerPoint.run context created, loading presentation...`);
            const presentation = context.presentation;
            const slides = presentation.slides;
            
            // Load all slides
            slides.load("items");
            await context.sync();
            
            console.log(`[DocumentAdapter] Found ${slides.items.length} slides`);
            const allText: string[] = [];
            
            // OPTIMIZED: Batch load operations to minimize sync calls
            // Step 1: Load all shapes for all slides
            for (let i = 0; i < slides.items.length; i++) {
              const slide = slides.items[i];
              const shapes = slide.shapes;
              shapes.load("items");
            }
            
            // Single sync to get all shapes
            await context.sync();
            
            // Step 2: Store shapes.items references after loading and syncing
            // CRITICAL: Must store the items array immediately after sync to avoid proxy issues
            const shapesItemsArray: PowerPoint.Shape[][] = [];
            for (let i = 0; i < slides.items.length; i++) {
              const slide = slides.items[i];
              const shapes = slide.shapes;
              // Store the items array reference - this is now loaded and accessible
              shapesItemsArray.push(shapes.items);
            }
            
            // Step 3: Load all textRanges in batch (queue all load operations)
            const slideTextArrays: string[][] = [];
            for (let i = 0; i < slides.items.length; i++) {
              slideTextArrays.push([]);
            }
            
            for (let i = 0; i < slides.items.length; i++) {
              // Use the stored shapes.items array reference
              const shapesItems = shapesItemsArray[i];
              
              for (let j = 0; j < shapesItems.length; j++) {
                const shape = shapesItems[j];
                const textFrame = shape.textFrame;
                
                if (textFrame) {
                  const textFrameAny = textFrame as any;
                  // Queue textRange.text load for all shapes
                  try {
                    if (textFrameAny.textRange) {
                      // textRange already exists, just load text
                      textFrameAny.textRange.load("text");
                    } else {
                      // Need to load textRange first, then text
                      textFrame.load("textRange");
                    }
                  } catch (e) {
                    // Skip shapes that can't be loaded
                    continue;
                  }
                }
              }
            }
            
            // Single sync to get all textRange data
            await context.sync();
            
            // Step 4: Load text property for all textRanges that need it
            for (let i = 0; i < slides.items.length; i++) {
              // Use the stored shapes.items array reference
              const shapesItems = shapesItemsArray[i];
              
              for (let j = 0; j < shapesItems.length; j++) {
                const shape = shapesItems[j];
                const textFrame = shape.textFrame;
                
                if (textFrame) {
                  const textFrameAny = textFrame as any;
                  try {
                    // If textRange exists, load its text property
                    if (textFrameAny.textRange) {
                      textFrameAny.textRange.load("text");
                    }
                  } catch (e) {
                    // Skip if can't load
                  }
                }
              }
            }
            
            // Final sync to get all text values
            await context.sync();
            
            // Step 5: Extract text from all loaded textRanges (no more syncs needed)
            for (let i = 0; i < slides.items.length; i++) {
              // Use the stored shapes.items array reference
              const shapesItems = shapesItemsArray[i];
              
              for (let j = 0; j < shapesItems.length; j++) {
                const shape = shapesItems[j];
                const textFrame = shape.textFrame;
                
                if (textFrame) {
                  const textFrameAny = textFrame as any;
                  try {
                    // Extract text from textRange
                    const text = textFrameAny.textRange?.text || "";
                    
                    if (text && text.trim() !== "") {
                      slideTextArrays[i].push(text.trim());
                    }
                  } catch (e) {
                    // Skip if extraction fails
                  }
                }
              }
            }
            
            // Combine slide texts
            for (let i = 0; i < slideTextArrays.length; i++) {
              if (slideTextArrays[i].length > 0) {
                allText.push(`Slide ${i + 1}:\n${slideTextArrays[i].join("\n")}`);
              }
            }
            
            const combinedText = allText.join("\n\n");
            console.log(`[DocumentAdapter] PowerPoint content retrieved, length:`, combinedText.length);
            resolve(combinedText);
          } catch (error) {
            console.error(`[DocumentAdapter] Error in PowerPoint.run:`, error);
            console.error(`[DocumentAdapter] PowerPoint.run error details:`, {
              error: error,
              errorMessage: error instanceof Error ? error.message : String(error),
              errorStack: error instanceof Error ? error.stack : undefined
            });
            resolve("");
          }
        }).catch((error) => {
          // Fallback if PowerPoint.run fails
          console.error(`[DocumentAdapter] PowerPoint.run promise rejected:`, error);
          console.error(`[DocumentAdapter] PowerPoint.run rejection details:`, {
            error: error,
            errorMessage: error instanceof Error ? error.message : String(error),
            errorStack: error instanceof Error ? error.stack : undefined
          });
          resolve("");
        });
      });
    }
    
    // Excel-specific: Get workbook content using Excel.run
    if (this.host === "Excel") {
      console.log(`[DocumentAdapter] Excel host detected, using Excel.run API`);
      return new Promise((resolve) => {
        console.log(`[DocumentAdapter] Checking Excel API availability...`);
        console.log(`[DocumentAdapter] typeof Excel:`, typeof Excel);
        
        if (typeof Excel === 'undefined') {
          console.warn(`[DocumentAdapter] Excel API not available (typeof Excel === 'undefined')`);
          resolve("");
          return;
        }
        
        console.log(`[DocumentAdapter] Excel API available, calling Excel.run...`);
        Excel.run(async (context) => {
          try {
            console.log(`[DocumentAdapter] Excel.run context created, loading active worksheet...`);
            const worksheet = context.workbook.worksheets.getActiveWorksheet();
            worksheet.load("name");
            await context.sync();
            
            console.log(`[DocumentAdapter] Active worksheet: ${worksheet.name}`);
            
            // Get the used range (all cells with data)
            const usedRange = worksheet.getUsedRange();
            
            // Check if there's any data
            if (!usedRange) {
              console.log(`[DocumentAdapter] No used range found, worksheet is empty`);
              resolve(`Worksheet: ${worksheet.name}\n\n(Empty worksheet)`);
              return;
            }
            
            // Load the values from the used range
            usedRange.load("values");
            await context.sync();
            
            console.log(`[DocumentAdapter] Used range loaded, rows: ${usedRange.values.length}, cols: ${usedRange.values[0]?.length || 0}`);
            
            // Format the data as a readable table
            const formattedData = formatExcelData(worksheet.name, usedRange.values);
            
            console.log(`[DocumentAdapter] Excel content retrieved, length:`, formattedData.length);
            resolve(formattedData);
          } catch (error) {
            console.error(`[DocumentAdapter] Error in Excel.run:`, error);
            console.error(`[DocumentAdapter] Excel.run error details:`, {
              error: error,
              errorMessage: error instanceof Error ? error.message : String(error),
              errorStack: error instanceof Error ? error.stack : undefined
            });
            resolve("");
          }
        }).catch((error) => {
          // Fallback if Excel.run fails
          console.error(`[DocumentAdapter] Excel.run promise rejected:`, error);
          console.error(`[DocumentAdapter] Excel.run rejection details:`, {
            error: error,
            errorMessage: error instanceof Error ? error.message : String(error),
            errorStack: error instanceof Error ? error.stack : undefined
          });
          resolve("");
        });
      });
    }

    // Default fallback for others
    console.log(`[DocumentAdapter] Non-Word/PowerPoint/Excel host (${this.host}), returning empty string`);
    return ""; 
  }

  async getSelection(): Promise<string> {
    console.log(`[DocumentAdapter] getSelection() called for host: ${this.host}`);
    
    // Word-specific: Use Word.js API for Word Online
    if (this.host === "Word") {
      console.log(`[DocumentAdapter] Word host detected, using Word.run API for selection`);
      return new Promise((resolve) => {
        if (typeof Word === 'undefined') {
          console.warn(`[DocumentAdapter] Word API not available (typeof Word === 'undefined')`);
          resolve("");
          return;
        }
        
        console.log(`[DocumentAdapter] Word API available, calling Word.run for selection...`);
        Word.run(async (context) => {
          try {
            console.log(`[DocumentAdapter] Word.run context created, getting selection...`);
            // Get the current selection from the document
            const range = context.document.getSelection();
            
            // Queue a command to load the 'text' property of the selection
            range.load('text');
            
            // Synchronize the document state with the JavaScript proxy objects
            await context.sync();
            
            // The highlighted text is now available in range.text
            const textSelected = range.text || "";
            console.log(`[DocumentAdapter] Selection retrieved, length:`, textSelected.length);
            resolve(textSelected);
          } catch (error) {
            console.error(`[DocumentAdapter] Error in Word.run for selection:`, error);
            console.error(`[DocumentAdapter] Word.run selection error details:`, {
              error: error,
              errorMessage: error instanceof Error ? error.message : String(error),
              errorStack: error instanceof Error ? error.stack : undefined
            });
            resolve(""); // Return empty if error (e.g., no selection)
          }
        }).catch((error) => {
          // Fallback if Word.run fails
          console.error(`[DocumentAdapter] Word.run promise rejected for selection:`, error);
          resolve("");
        });
      });
    }
    
    // Excel-specific: Use Excel.js API for Excel
    if (this.host === "Excel") {
      console.log(`[DocumentAdapter] Excel host detected, using Excel.run API for selection`);
      return new Promise((resolve) => {
        if (typeof Excel === 'undefined') {
          console.warn(`[DocumentAdapter] Excel API not available (typeof Excel === 'undefined')`);
          resolve("");
          return;
        }
        
        console.log(`[DocumentAdapter] Excel API available, calling Excel.run for selection...`);
        Excel.run(async (context) => {
          try {
            console.log(`[DocumentAdapter] Excel.run context created, getting selection...`);
            
            // Get the selected range from the workbook
            const selectedRange = context.workbook.getSelectedRange();
            
            // Load the values from the selected range
            selectedRange.load("values");
            
            // Synchronize the document state
            await context.sync();
            
            // Format the selected cell values
            if (selectedRange.values && selectedRange.values.length > 0) {
              const formattedSelection = formatExcelData("Selected Range", selectedRange.values);
              console.log(`[DocumentAdapter] Selection retrieved, length:`, formattedSelection.length);
              resolve(formattedSelection);
            } else {
              console.log(`[DocumentAdapter] No cells selected or selection is empty`);
              resolve(""); // Return empty if nothing selected
            }
          } catch (error) {
            console.error(`[DocumentAdapter] Error in Excel.run for selection:`, error);
            console.error(`[DocumentAdapter] Excel.run selection error details:`, {
              error: error,
              errorMessage: error instanceof Error ? error.message : String(error),
              errorStack: error instanceof Error ? error.stack : undefined
            });
            resolve(""); // Return empty if error (e.g., no selection)
          }
        }).catch((error) => {
          // Fallback if Excel.run fails
          console.error(`[DocumentAdapter] Excel.run promise rejected for selection:`, error);
          resolve("");
        });
      });
    }
    
    // For other hosts (PowerPoint, etc.), use Office.js API
    console.log(`[DocumentAdapter] Non-Word/Excel host (${this.host}), using Office.js API for selection`);
    console.log(`[DocumentAdapter] Office.context.document available:`, !!Office.context?.document);
    console.log(`[DocumentAdapter] getSelectedDataAsync available:`, typeof Office.context?.document?.getSelectedDataAsync);
    
    return new Promise((resolve) => {
      if (!Office.context?.document?.getSelectedDataAsync) {
        console.warn(`[DocumentAdapter] getSelectedDataAsync not available`);
        resolve("");
        return;
      }
      
      console.log(`[DocumentAdapter] Calling getSelectedDataAsync...`);
      Office.context.document.getSelectedDataAsync(Office.CoercionType.Text, (result) => {
        console.log(`[DocumentAdapter] getSelectedDataAsync result:`, {
          status: result.status,
          succeeded: result.status === Office.AsyncResultStatus.Succeeded,
          hasValue: !!result.value
        });
        
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const value = result.value as string;
          console.log(`[DocumentAdapter] Selection retrieved, length:`, value.length);
          resolve(value);
        } else {
          console.log(`[DocumentAdapter] Selection failed or empty, status:`, result.status);
          if (result.error) {
            console.error(`[DocumentAdapter] Selection error:`, result.error);
          }
          resolve(""); // Return empty if nothing selected
        }
      });
    });
  }
}